// Silence noisy logs in production (keep warn/error)
import './utils/prodConsoleSilencer';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ColorModeScript } from '@chakra-ui/react';
import './index.css';
import './styles/variables.css'; // Import CSS variables with dark mode support
import './styles/base.css'; // Import base styles
import './styles/global.css'; // Import our global styles
// import './styles/viewport-fix.css'; // DISABLED - was causing scroll issues on home page
import App from './App';
import theme from './theme';
import reportWebVitals from './reportWebVitals';
import { PostHogProvider } from 'posthog-js/react';
import { getEnvVar } from './api/config';

// Get PostHog configuration from Infisical-managed environment variables
const POSTHOG_API_KEY = getEnvVar('VITE_PUBLIC_POSTHOG_KEY');
const POSTHOG_HOST = getEnvVar('VITE_PUBLIC_POSTHOG_HOST', 'https://us.posthog.com');
const IS_DEVELOPMENT = getEnvVar('VITE_ENV', 'development') === 'development';

// PostHog configuration loaded

// Validate required PostHog configuration
if (!POSTHOG_API_KEY) {
  console.error('❌ PostHog API Key is missing! Check your Infisical configuration for VITE_PUBLIC_POSTHOG_KEY');
}

if (!POSTHOG_HOST) {
  console.error('❌ PostHog Host is missing! Check your Infisical configuration for VITE_PUBLIC_POSTHOG_HOST');
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Global handlers to gracefully recover from dynamic import and preload errors
(() => {
  const reloadOnce = (key: string) => {
    try {
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return true;
      }
    } catch (_) {
      // If sessionStorage fails, still attempt reload
      window.location.reload();
      return true;
    }
    return false;
  };

  // Vite-specific preload failure
  window.addEventListener('vite:preloadError' as any, () => {
    reloadOnce('vite-preload-reload');
  });

  // Module import failures and MIME mismatch
  window.addEventListener('error', (evt: ErrorEvent) => {
    const msg = evt?.message || '';
    if (
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg) ||
      /Expected a (JavaScript|module) .* MIME type/i.test(msg) ||
      /script has an unsupported MIME type/i.test(msg)
    ) {
      if (reloadOnce('module-import-reload')) return;
    }
  });

  // Catch rejected dynamic import() promises
  window.addEventListener('unhandledrejection', (evt: PromiseRejectionEvent) => {
    const reason: any = evt?.reason || {};
    const msg = typeof reason === 'string' ? reason : reason?.message || '';
    if (
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg) ||
      /Loading chunk .* failed/i.test(msg)
    ) {
      if (reloadOnce('import-rejection-reload')) return;
    }
  });
})();
root.render(
  <React.StrictMode>
    <ColorModeScript initialColorMode={theme.config.initialColorMode} />
    <PostHogProvider
      apiKey={POSTHOG_API_KEY || ''}
      options={{
        api_host: POSTHOG_HOST,
        capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
        debug: false,
        persistence: 'localStorage+cookie',
        autocapture: true,
        capture_pageview: true,
        loaded: (posthog) => {
          // PostHog loaded successfully
          
          // PostHog initialized successfully
        }
      }}
    >
      <App />
    </PostHogProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// Reduce console noise in production: only log vitals in non-prod
try {
  const isProdEnv = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_ENV === 'production')
    || (typeof window !== 'undefined' && (window as any)?.ENV?.VITE_ENV === 'production');
  reportWebVitals(isProdEnv ? undefined : console.log);
} catch {
  reportWebVitals(console.log);
}
